import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { GetServiceCategory } from "../../Services";

const AddServiceCategory = ({
  editingCategory,
  setEditingCategory,
  setCategories,
  setShowModal,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    base_price: "",
    price_per_km: "",
    price_per_minute: "",
    waiting_charge: "",
    trip_price_multiplier: 1,
    cancellation_fee: "",
    min_distance: "",
    max_passengers: "",
    luggage_allowance: "",
    icon_url: "",
  });

  /* =========================
     Init edit mode
  ========================== */
  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name || "",
        description: editingCategory.description || "",
        base_price: editingCategory.base_price ?? "",
        price_per_km: editingCategory.price_per_km ?? "",
        price_per_minute: editingCategory.price_per_minute ?? "",
        waiting_charge: editingCategory.waiting_charge ?? "",
        trip_price_multiplier: editingCategory.trip_price_multiplier ?? 1,
        cancellation_fee: editingCategory.cancellation_fee ?? "",
        min_distance: editingCategory.min_distance ?? "",
        max_passengers: editingCategory.max_passengers ?? "",
        luggage_allowance: editingCategory.luggage_allowance ?? "",
        icon_url: editingCategory.icon_url || "",
      });
    }
  }, [editingCategory]);

  /* =========================
     Handle change
  ========================== */
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  /* =========================
     Submit
  ========================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Service category name is required");
      return;
    }

    try {
      const payload = {
        ...formData,
        base_price: Number(formData.base_price || 0),
        price_per_km: Number(formData.price_per_km || 0),
        price_per_minute: Number(formData.price_per_minute || 0),
        waiting_charge: Number(formData.waiting_charge || 0),
        trip_price_multiplier: Number(formData.trip_price_multiplier || 1),
        cancellation_fee: Number(formData.cancellation_fee || 0),
        min_distance: Number(formData.min_distance || 0),
        max_passengers: formData.max_passengers
          ? Number(formData.max_passengers)
          : null,
        luggage_allowance: formData.luggage_allowance
          ? Number(formData.luggage_allowance)
          : null,
      };

      let res;

      if (editingCategory) {
        res = await GetServiceCategory.updateServiceCategory(
          editingCategory.id,
          payload,
        );
      } else {
        res = await GetServiceCategory.createServiceCategory(payload);
      }

      const savedCategory = res.data; // ✅ this is the DB object

      console.log("Data: ", savedCategory);
      toast.success(
        editingCategory
          ? "Service category updated"
          : "Service category created",
      );

      /* =========================
       Update parent table
    ========================== */
      if (setCategories) {
        setCategories((prev) => {
          if (editingCategory) {
            return prev.map((c) =>
              c.id === savedCategory.id ? savedCategory : c,
            );
          }
          return [savedCategory, ...prev];
        });
      }

      /* =========================
       Reset & close modal
    ========================== */
      setEditingCategory(null);

      // small delay feels nicer UX-wise
      if (setShowModal) {
        setTimeout(() => setShowModal(false), 1500);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to save service category -",
      );
    }
  };

  return (
    <div
      className="max-w-lg mx-auto bg-white p-6 shadow-md rounded-xl"
      style={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      <h2 className="text-xl font-semibold mb-4">
        {editingCategory ? "Edit Service Category" : "Add Service Category"}
      </h2>

      <form onSubmit={handleSubmit}>
        {/* Name */}
        <div className="form-group mb-4">
          <label htmlFor="name">Name *</label>
          <input
            id="name"
            className="form-control"
            value={formData.name}
            onChange={handleChange}
            placeholder="ECONOMY / PREMIUM / LUXURY"
            required
          />
        </div>

        {/* Description */}
        <div className="form-group mb-4">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            className="form-control"
            value={formData.description}
            onChange={handleChange}
            rows={2}
          />
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-2 gap-3">
          <InputField
            id="base_price"
            label="Base Price"
            value={formData.base_price}
            onChange={handleChange}
          />
          <InputField
            id="price_per_km"
            label="Price / KM"
            value={formData.price_per_km}
            onChange={handleChange}
          />
          <InputField
            id="price_per_minute"
            label="Price / Minute"
            value={formData.price_per_minute}
            onChange={handleChange}
          />
          <InputField
            id="waiting_charge"
            label="Waiting / Minute"
            value={formData.waiting_charge}
            onChange={handleChange}
          />
        </div>

        {/* Other configs */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <InputField
            id="trip_price_multiplier"
            label="Price Multiplier"
            value={formData.trip_price_multiplier}
            onChange={handleChange}
          />
          <InputField
            id="cancellation_fee"
            label="Cancellation Fee"
            value={formData.cancellation_fee}
            onChange={handleChange}
          />
          <InputField
            id="min_distance"
            label="Min Distance (KM)"
            value={formData.min_distance}
            onChange={handleChange}
          />
          <InputField
            id="max_passengers"
            label="Max Passengers"
            value={formData.max_passengers}
            onChange={handleChange}
          />
        </div>

        <div className="form-group mb-4 mt-3">
          <label htmlFor="luggage_allowance">Luggage Allowance (KG)</label>
          <input
            id="luggage_allowance"
            className="form-control"
            value={formData.luggage_allowance}
            onChange={handleChange}
          />
        </div>

        <div className="form-group mb-4">
          <label htmlFor="icon_url">Icon URL</label>
          <input
            id="icon_url"
            className="form-control"
            value={formData.icon_url}
            onChange={handleChange}
          />
        </div>

        <div className="d-flex justify-content-between">
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setEditingCategory(null);
              setShowModal(false);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

/* =========================
   Reusable input field
========================== */
const InputField = ({ id, label, value, onChange }) => (
  <div className="form-group mb-3">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      type="number"
      className="form-control"
      value={value}
      onChange={onChange}
    />
  </div>
);

export default AddServiceCategory;
